import { useEffect, useState } from 'react';
import { Banner, Button, Card, Page, Select, Text, TextField } from '@shopify/polaris';
import { MetricCard } from '../components/MetricCard';
import { StatusBadge } from '../components/StatusBadge';

const STATUS_OPTIONS = [
  { label: 'Усі статуси', value: '' },
  { label: 'Очікує оплату', value: 'waiting_payment' },
  { label: 'У черзі', value: 'queued' },
  { label: 'Створюється', value: 'processing' },
  { label: 'Повтор заплановано', value: 'retry_scheduled' },
  { label: 'ТТН створена', value: 'created' },
  { label: 'Помилка', value: 'failed' },
  { label: 'Пропущено', value: 'skipped' },
  { label: 'Ручний режим', value: 'manual_hold' },
  { label: 'Тестовий режим', value: 'dry_run' },
];

const DELIVERY_LABELS: Record<string, string> = {
  branch: 'Відділення',
  postomat: 'Поштомат',
  courier: 'Курʼєр',
};

const PAYMENT_LABELS: Record<string, string> = {
  liqpay_online: 'LiqPay онлайн',
  nova_cod: 'Оплата на пошті',
  pickup_cash: 'Оплата в ТЦ',
};

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('uk-UA');
}

function formatMoneyCents(value?: number | null) {
  if (value === null || value === undefined) return '—';
  return `${(Number(value || 0) / 100).toLocaleString('uk-UA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} грн`;
}

function normalizeMoneyCents(value?: number | null) {
  return Number.isFinite(Number(value)) ? Number(value || 0) : 0;
}

function sumItemsCents(items: any[] = []) {
  return items.reduce((sum, item) => sum + normalizeMoneyCents(item.finalLinePrice ?? item.final_line_price), 0);
}

function getSenderAddressOptions(directory: any) {
  const addresses = Array.isArray(directory?.addresses) ? directory.addresses : [];
  return [
    { label: 'Оберіть місце відправки', value: '' },
    ...addresses.map((address: any) => ({
      label: [address.city, address.label || address.description || address.ref].filter(Boolean).join(' · '),
      value: address.ref,
    })),
  ];
}

function getDefaultSenderPayload(directory: any, addressRef = '') {
  const sender = directory?.selectedSender || (Array.isArray(directory?.senders) ? directory.senders[0] : null) || {};
  const contacts = Array.isArray(directory?.contacts) ? directory.contacts : [];
  const addresses = Array.isArray(directory?.addresses) ? directory.addresses : [];
  const address = addresses.find((item: any) => item.ref === addressRef) || null;
  const contact = contacts[0] || {};

  return {
    senderRef: sender.ref || '',
    senderCityRef: address?.cityRef || sender.cityRef || '',
    senderAddressRef: address?.ref || '',
    senderAddressLabel: address ? [address.city, address.label || address.description].filter(Boolean).join(' · ') : '',
    senderContactRef: contact.ref || '',
    senderPhone: contact.phone || '',
    sourceLabel: address ? [address.city, address.label || address.description].filter(Boolean).join(' · ') : '',
  };
}

function presentError(value?: string | null) {
  if (!value) return '—';
  if (/too many|to many|429|rate limit|обмежила кількість/u.test(value.toLowerCase())) {
    return 'Нова Пошта тимчасово обмежила запити. Система повторить пізніше.';
  }
  if (/timeout|timed out|fetch failed|econnreset|etimedout|socket hang up/u.test(value.toLowerCase())) {
    return 'Тимчасовий збій звʼязку з Новою Поштою.';
  }
  return value;
}

function retryTitle(row: any) {
  if (row?.status === 'created') return 'ТТН створена';
  if (row?.status === 'retry_scheduled') return 'Повтор заплановано';
  if (row?.status === 'queued') return 'Повернуто в чергу';
  if (row?.status === 'failed') return 'Повтор завершився помилкою';
  if (row?.status === 'skipped') return 'Пропущено';
  return 'Спробу виконано';
}

function retryMessage(row: any) {
  if (row?.documentNumber) return `ТТН ${row.documentNumber}`;
  if (row?.lastError) return presentError(row.lastError);
  if (row?.nextAttemptAt) return `Наступна спроба: ${formatDateTime(row.nextAttemptAt)}`;
  if (row?.statusLabel) return `Новий статус: ${row.statusLabel}`;
  return 'Статус оновлено.';
}

function getTtnNumbers(row: any) {
  return [
    row.documentNumber,
    ...(row.shipments || []).map((shipment: any) => shipment.documentNumber),
  ].filter(Boolean).join(', ');
}

function deliveryPaymentHint(row: any) {
  if (row?.deliveryPayerType === 'Recipient') {
    const price = row.deliveryPriceCents ? ` Орієнтир НП: ${formatMoneyCents(row.deliveryPriceCents)}.` : '';
    return `Клієнт сплачує доставку Новій Пошті.${price}`;
  }
  if (row?.deliveryPayerType === 'Sender' || row?.freeShipping) {
    const price = row.deliveryPriceCents ? ` Орієнтир НП: ${formatMoneyCents(row.deliveryPriceCents)}.` : '';
    return `Магазин сплачує доставку. Для клієнта безкоштовно.${price}`;
  }
  return `Shipping у Shopify: ${formatMoneyCents(row?.shippingCents)}`;
}

function DetailItem({ label, value, hint, code = false }: any) {
  return (
    <div className="admin-np-detail">
      <div className="admin-np-detail__label">{label}</div>
      <div className={code ? 'admin-np-detail__value admin-np-detail__value--code' : 'admin-np-detail__value'}>
        {value || '—'}
      </div>
      {hint ? <div className="admin-np-detail__hint">{hint}</div> : null}
    </div>
  );
}

export function NovaPoshtaOrdersPage({ api, readOnly = false, notify }: any) {
  const [report, setReport] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [busyShipmentId, setBusyShipmentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [senderDirectory, setSenderDirectory] = useState<any>(null);
  const [shipmentDrafts, setShipmentDrafts] = useState<Record<string, any>>({});

  const refresh = async (nextStatus = statusFilter) => {
    setLoading(true);
    try {
      const nextReport = await api.getNovaPoshtaOrders(100, nextStatus || undefined);
      setReport(nextReport);
      setError('');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося завантажити Нова Пошта замовлення');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    api.getNovaPoshtaSenderDirectory()
      .then((directory: any) => setSenderDirectory(directory))
      .catch(() => setSenderDirectory(null));
  }, []);

  const updateShipmentDraft = (rowId: number, patch: any) => {
    setShipmentDrafts((current) => ({
      ...current,
      [rowId]: {
        ...(current[rowId] || {}),
        ...patch,
      },
    }));
  };

  const createShipment = async (row: any) => {
    const draft = shipmentDrafts[row.id] || {};
    const senderPayload = getDefaultSenderPayload(senderDirectory, draft.senderAddressRef || '');
    setBusyId(row.id);
    try {
      const result = await api.createNovaPoshtaShipment(row.id, {
        ...senderPayload,
        ...draft,
        sourceLabel: draft.sourceLabel || senderPayload.sourceLabel,
        itemSummary: draft.itemSummary || row.itemSummary,
        items: row.items || [],
        declaredValueCents: sumItemsCents(row.items || []) || row.deliveryPriceCents || 0,
      });
      notify?.({
        tone: 'success',
        title: 'Відправлення додано',
        message: result?.row?.sourceLabel || 'Чернетку створено.',
      });
      setShipmentDrafts((current) => ({ ...current, [row.id]: {} }));
      await refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося створити відправлення');
    } finally {
      setBusyId(null);
    }
  };

  const createShipmentTtn = async (shipment: any) => {
    setBusyShipmentId(shipment.id);
    try {
      const result = await api.createNovaPoshtaShipmentTtn(shipment.id);
      notify?.({
        tone: result?.row?.status === 'created' ? 'success' : (result?.row?.status === 'failed' ? 'critical' : 'info'),
        title: result?.row?.documentNumber ? `ТТН ${result.row.documentNumber}` : (result?.row?.statusLabel || 'Статус оновлено'),
        message: result?.row?.lastError || result?.row?.sourceLabel || 'Відправлення оновлено.',
      });
      await refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося створити ТТН');
    } finally {
      setBusyShipmentId(null);
    }
  };

  const syncShopify = async (row: any) => {
    setBusyId(row.id);
    try {
      await api.syncNovaPoshtaShipmentsToShopify(row.shopifyOrderId);
      notify?.({
        tone: 'success',
        title: 'Shopify order оновлено',
        message: row.shopifyOrderName || row.shopifyOrderId,
      });
      await refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося записати дані в Shopify order');
    } finally {
      setBusyId(null);
    }
  };

  const retry = async (row: any) => {
    setBusyId(row.id);
    try {
      const result = await api.retryNovaPoshtaOrder(row.id);
      notify?.({
        tone: result?.row?.status === 'created' ? 'success' : (result?.row?.status === 'failed' ? 'critical' : 'info'),
        title: retryTitle(result?.row),
        message: retryMessage(result?.row),
      });
      await refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Повторити створення ТТН не вдалося');
    } finally {
      setBusyId(null);
    }
  };

  const archiveHistory = async () => {
    if (!window.confirm('Очистити історію НП job-ів? Активна черга не буде зачеплена, terminal rows просто сховаються зі списку.')) {
      return;
    }

    setLoading(true);
    try {
      const result = await api.archiveNovaPoshtaOrderHistory();
      notify?.({
        tone: 'success',
        title: 'Історію очищено',
        message: `Сховано записів: ${result?.archivedCount ?? 0}`,
      });
      await refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Очистити історію НП job-ів не вдалося');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page fullWidth title="Нова Пошта замовлення" subtitle="ТТН, помилки створення і ручний повтор">
      <div className="admin-stack">
        {error ? <Banner tone="critical" title="Нова Пошта список недоступний"><p>{presentError(error)}</p></Banner> : null}
        {readOnly ? (
          <Banner tone="info" title="Роль перегляду">
            <p>Ручне повторення створення ТТН вимкнене для ролі “Перегляд”.</p>
          </Banner>
        ) : null}
        <Banner tone="info" title="Як працюють статуси">
          <p>
            Основний production-флоу ручний: спочатку створюється Shopify order, потім менеджер додає одне або кілька відправлень, вибирає місце відправки і створює ТТН. Старий auto-job лишився тільки для історичних помилок і черги.
          </p>
        </Banner>

        <div className="admin-grid admin-grid--metrics">
          <MetricCard label="ТТН створено" value={String(report?.summary?.createdCount ?? 0)} summary="Є номер і Ref Нової Пошти" status="ok" />
          <MetricCard label="Потребують уваги" value={String(report?.summary?.attentionCount ?? 0)} summary="Очікують оплату, чергу або retry" status={report?.summary?.attentionCount ? 'warning' : 'ok'} />
          <MetricCard label="Помилки" value={String(report?.summary?.failedCount ?? 0)} summary="Потрібна ручна перевірка" status={report?.summary?.failedCount ? 'critical' : 'ok'} />
          <MetricCard label="У процесі" value={String(report?.summary?.processingCount ?? 0)} summary="Worker зараз створює ТТН" status={report?.summary?.processingCount ? 'running' : 'ok'} />
          <MetricCard label="Відправлення" value={String(report?.summary?.shipmentCount ?? 0)} summary="Окремі посилки всередині order-ів" status="ok" />
        </div>

        <Card>
          <div className="admin-action-card">
            <div className="admin-section-head">
              <Text as="h2" variant="headingMd">Фільтр і оновлення</Text>
              <div className="admin-inline-actions">
                <Button onClick={() => refresh()} loading={loading}>Оновити</Button>
                <Button onClick={archiveHistory} disabled={readOnly || loading}>Очистити історію</Button>
              </div>
            </div>
            <Select
              label="Статус"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                void refresh(value);
              }}
            />
          </div>
        </Card>

        <Card>
          <div className="admin-stack">
            <Text as="h2" variant="headingMd">Останні НП job-и</Text>
            <div className="admin-np-orders">
              {(report?.rows || []).map((row: any) => {
                const draft = shipmentDrafts[row.id] || {};
                const addressOptions = getSenderAddressOptions(senderDirectory);
                const ttnNumbers = getTtnNumbers(row);
                return (
                  <div className="admin-np-order-card" key={row.id}>
                    <div className="admin-np-order-card__head">
                      <div className="admin-np-order-card__title">
                        {row.orderStatusUrl ? (
                          <a href={row.orderStatusUrl} target="_blank" rel="noreferrer">
                            {row.shopifyOrderName || row.shopifyOrderId}
                          </a>
                        ) : (
                          row.shopifyOrderName || row.shopifyOrderId
                        )}
                        <div className="admin-table__hint">{formatDateTime(row.createdAt)}</div>
                      </div>
                      <div className="admin-np-order-card__badges">
                        <StatusBadge status={row.statusTone} label={row.statusLabel} />
                        <span className="admin-code-label">{(row.shipments || []).length} відпр.</span>
                      </div>
                    </div>

                    <div className="admin-np-order-grid">
                      <DetailItem
                        label="Клієнт"
                        value={row.customerName}
                        hint={row.customerPhone}
                      />
                      <DetailItem
                        label="Доставка"
                        value={DELIVERY_LABELS[row.deliveryType] || row.deliveryType}
                        hint={[row.cityLabel, row.destinationLabel].filter(Boolean).join(' · ')}
                      />
                      <DetailItem
                        label="Оплата"
                        value={PAYMENT_LABELS[row.paymentMethod] || row.paymentMethod}
                        hint={deliveryPaymentHint(row)}
                      />
                      <DetailItem
                        label="ТТН"
                        value={ttnNumbers}
                        hint={[row.costOnSite ? `НП: ${row.costOnSite} грн` : '', row.estimatedDeliveryDate].filter(Boolean).join(' · ')}
                        code
                      />
                      <DetailItem
                        label="Стара auto-спроба"
                        value={`${row.attemptCount}/${row.maxAttempts}`}
                        hint={row.nextAttemptAt ? `Наступна: ${formatDateTime(row.nextAttemptAt)}` : row.statusLabel}
                      />
                      <DetailItem
                        label="Помилка"
                        value={presentError(row.lastError)}
                      />
                    </div>

                    <div className="admin-np-order-actions">
                      <div className="admin-np-action">
                        <div>
                          <div className="admin-np-action__title">Стара auto-ТТН job</div>
                          <div className="admin-np-action__hint">Для історичних помилок worker-а; ручний режим не чіпає.</div>
                        </div>
                        <Button
                          size="slim"
                          onClick={() => retry(row)}
                          loading={busyId === row.id}
                          disabled={readOnly || !row.retryable || busyId !== null}
                        >
                          Повторити auto-job
                        </Button>
                      </div>
                      <div className="admin-np-action">
                        <div>
                          <div className="admin-np-action__title">Shopify order</div>
                          <div className="admin-np-action__hint">Оновлює ТТН і джерела в Additional details.</div>
                        </div>
                        <Button
                          size="slim"
                          onClick={() => syncShopify(row)}
                          loading={busyId === row.id}
                          disabled={readOnly || busyId !== null || !(row.shipments || []).length}
                        >
                          Синхронізувати Shopify
                        </Button>
                      </div>
                    </div>

                    <div className="admin-shipment-panel">
                      <div className="admin-section-head">
                        <Text as="h3" variant="headingSm">Відправлення з магазинів</Text>
                        <div className="admin-table__hint">{row.itemSummary || 'Позиції з checkout payload'}</div>
                      </div>
                      <div className="admin-shipment-list">
                        {(row.shipments || []).map((shipment: any) => (
                          <div className="admin-shipment-card" key={shipment.id}>
                            <div className="admin-shipment-card__body">
                              <div className="admin-shipment-card__top">
                                <StatusBadge status={shipment.statusTone} label={shipment.statusLabel} />
                                <span className="admin-code-label">{shipment.documentNumber || 'ТТН ще немає'}</span>
                              </div>
                              <div className="admin-shipment-card__grid">
                                <DetailItem label="Звідки" value={shipment.sourceLabel || shipment.senderAddressLabel} />
                                <DetailItem label="Позиції" value={shipment.itemSummary} />
                                <DetailItem label="Оціночна вартість" value={formatMoneyCents(shipment.declaredValueCents)} />
                                <DetailItem label="Помилка" value={shipment.lastError ? presentError(shipment.lastError) : '—'} />
                              </div>
                            </div>
                            <Button
                              size="slim"
                              onClick={() => createShipmentTtn(shipment)}
                              loading={busyShipmentId === shipment.id}
                              disabled={readOnly || !shipment.canCreateTtn || busyShipmentId !== null}
                            >
                              Створити ТТН
                            </Button>
                          </div>
                        ))}
                        {(row.shipments || []).length === 0 ? (
                          <div className="admin-np-empty">Відправлень ще немає. Виберіть магазин/місто відправки і додайте першу посилку.</div>
                        ) : null}
                      </div>
                      <div className="admin-shipment-form">
                        <Select
                          label="Місце відправки"
                          options={addressOptions}
                          value={draft.senderAddressRef || ''}
                          onChange={(value) => {
                            const senderPayload = getDefaultSenderPayload(senderDirectory, value);
                            updateShipmentDraft(row.id, {
                              ...senderPayload,
                              senderAddressRef: value,
                              sourceLabel: senderPayload.sourceLabel,
                            });
                          }}
                          disabled={readOnly || addressOptions.length <= 1}
                        />
                        <TextField
                          label="Позиції / коментар"
                          value={draft.itemSummary || row.itemSummary || ''}
                          onChange={(value) => updateShipmentDraft(row.id, { itemSummary: value })}
                          autoComplete="off"
                          disabled={readOnly}
                        />
                        <Button
                          onClick={() => createShipment(row)}
                          loading={busyId === row.id}
                          disabled={readOnly || busyId !== null || !draft.senderAddressRef}
                        >
                          Додати відправлення
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {report && (report.rows || []).length === 0 ? (
                <div className="admin-empty-state">Немає НП job-ів для поточного фільтра.</div>
              ) : null}
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
}
